/**
 * Global store reset — clears all Zustand stores on sign-out.
 *
 * Uses Zustand's getInitialState() to restore each store to its
 * creation-time defaults without needing per-store reset methods.
 */

import type { StoreApi } from 'zustand'
import { useActivityStore } from '@/stores/activity-store'
import { useActiveJobsStore } from '@/stores/active-jobs-store'
import { useAdminStore } from '@/stores/admin-store'
import { useAgentStore } from '@/stores/agent-store'
import { useAiEditsStore } from '@/stores/ai-edits-store'
import { useArtifactReviewsStore } from '@/stores/artifact-reviews-store'
import { useAssistantStore } from '@/stores/assistant-store'
import { useAssistantDockStore } from '@/stores/assistant-dock-store'
import { useBidMembersStore } from '@/stores/bid-members-store'
import { useBidMenuStore } from '@/stores/bid-menu-store'
import { useBidOverviewStore } from '@/stores/bid-overview-store'
import { useBidsStore } from '@/stores/bids-store'
import { useBriefScaffoldStore } from '@/stores/brief-scaffold-store'
import { useComplianceItemsStore } from '@/stores/compliance-items-store'
import { useCoverageStore } from '@/stores/coverage-store'
import { useDashboardStore } from '@/stores/dashboard-store'
import { useDocumentsStore } from '@/stores/documents-store'
import { useDraftingStore } from '@/stores/drafting-store'
import { useEvaluationCriteriaStore } from '@/stores/evaluation-criteria-store'
import { useExportTemplatesStore } from '@/stores/export-templates-store'
import { useExtractionStore } from '@/stores/extraction-store'
import { useFeedbackStore } from '@/stores/feedback-store'
import { useFullResponseStore } from '@/stores/full-response-store'
import { useHarvestStore } from '@/stores/harvest-store'
import { useHelpStore } from '@/stores/help-store'
import { useKeyDatesStore } from '@/stores/key-dates-store'
import { useLibraryStore } from '@/stores/library-store'
import { useMyWorkStore } from '@/stores/my-work-store'
import { useNextActionsStore } from '@/stores/next-actions-store'
import { useNotificationsStore } from '@/stores/notifications-store'
import { useOnboardingStore } from '@/stores/onboarding-store'
import { useOrgLifecycleStore } from '@/stores/org-lifecycle-store'
import { useOutlineProposalStore } from '@/stores/outline-proposal-store'
import { useOutlineTemplatesStore } from '@/stores/outline-templates-store'
import { usePdfExportStore } from '@/stores/pdf-export-store'
import { usePreviewStore } from '@/stores/preview-store'
import { usePromptStore } from '@/stores/prompt-store'
import { useQuestionsStore } from '@/stores/questions-store'
import { useReconciliationStore } from '@/stores/reconciliation-store'
import { useRequirementCriteriaLinksStore } from '@/stores/requirement-criteria-links-store'
import { useRequirementsStore } from '@/stores/requirements-store'
import { useResponsesStore } from '@/stores/responses-store'
import { useScheduleStore } from '@/stores/schedule-store'
import { useWordFormStore } from '@/stores/word-form-store'
import { useSectionAssignmentsStore } from '@/stores/section-assignments-store'
import { useSectionFlagsStore } from '@/stores/section-flags-store'
import { useSettingsStore } from '@/stores/settings-store'
import { useSharePointStore } from '@/stores/sharepoint-store'
import { useStrategyStore } from '@/stores/strategy-store'
import { useStructureProposalStore } from '@/stores/structure-proposal-store'
import { useStrategySettingsStore } from '@/stores/strategy-settings-store'
import { useSubmissionFilesStore } from '@/stores/submission-files-store'
import { useSubmissionPackStore } from '@/stores/submission-pack-store'
import { useSubmissionRequirementsStore } from '@/stores/submission-requirements-store'
import { useReviewStore } from '@/stores/review-store'
import { useCommentsStore } from '@/stores/comments-store'
import { useTenderChatStore } from '@/stores/tender-chat-store'
import { useTenderOverviewStore } from '@/stores/tender-overview-store'
import { useUnifiedAiPanelStore } from '@/stores/unified-ai-panel-store'
import { useUnitBriefStore } from '@/stores/unit-brief-store'

type Resettable = Pick<StoreApi<unknown>, 'setState' | 'getInitialState' | 'getState'>

// All stores except auth-store (which manages its own reset during sign-out)
const stores: Resettable[] = [
  useActivityStore,
  useActiveJobsStore,
  useAdminStore,
  useAgentStore,
  useAiEditsStore,
  useArtifactReviewsStore,
  useAssistantStore,
  useAssistantDockStore,
  useBidMembersStore,
  useBidMenuStore,
  useBidOverviewStore,
  useBidsStore,
  useBriefScaffoldStore,
  useComplianceItemsStore,
  useCoverageStore,
  useDashboardStore,
  useDocumentsStore,
  useDraftingStore,
  useEvaluationCriteriaStore,
  useExportTemplatesStore,
  useExtractionStore,
  useFeedbackStore,
  useFullResponseStore,
  useHarvestStore,
  useHelpStore,
  useKeyDatesStore,
  useLibraryStore,
  useMyWorkStore,
  useNextActionsStore,
  useNotificationsStore,
  useOnboardingStore,
  useOrgLifecycleStore,
  useOutlineProposalStore,
  useOutlineTemplatesStore,
  usePdfExportStore,
  usePreviewStore,
  usePromptStore,
  useQuestionsStore,
  useReconciliationStore,
  useRequirementCriteriaLinksStore,
  useRequirementsStore,
  useResponsesStore,
  useScheduleStore,
  useSectionAssignmentsStore,
  useSectionFlagsStore,
  useSettingsStore,
  useSharePointStore,
  useStrategyStore,
  useStructureProposalStore,
  useStrategySettingsStore,
  useSubmissionFilesStore,
  useSubmissionPackStore,
  useSubmissionRequirementsStore,
  useReviewStore,
  useCommentsStore,
  useTenderChatStore,
  useTenderOverviewStore,
  useUnifiedAiPanelStore,
  useUnitBriefStore,
  useWordFormStore,
]

function logResetFailure(stage: 'reset' | 'setState', index: number, error: unknown): void {
  console.error(
    JSON.stringify({
      timestamp: new Date().toISOString(),
      level: 'error',
      module: 'stores/reset',
      message: `Store ${stage}() failed during sign-out`,
      storeIndex: index,
      error: error instanceof Error ? error.message : String(error),
    })
  )
}

/**
 * Reset all application stores to their initial state.
 * Call this on sign-out to prevent data leaking between sessions.
 *
 * Every store is isolated. This loop is the sign-out data barrier, so a single
 * store throwing must never stop the rest from being cleared — one unhandled
 * throw here would leave the remaining stores holding the previous tenant's
 * data. reset() is the realistic thrower (sessionStorage access in a
 * storage-disabled browser, an abort-controller deref), so it is caught
 * separately and the state replace still runs for that store regardless.
 */
export function resetAllStores(): void {
  stores.forEach((store, index) => {
    // Several stores keep request de-duplication state (in-flight promises,
    // "already loaded" sentinels, abort controllers) at module scope, so it
    // survives a StrictMode remount. setState cannot reach any of it. Their
    // reset() clears it and orphans in-flight responses — without this, a
    // sign-out/sign-in inside a request window lets the previous user's
    // response land in the next user's store.
    try {
      const actions = store.getState() as { reset?: () => void }
      if (typeof actions.reset === 'function') actions.reset()
    } catch (error) {
      // Losing the module-scope cleanup for one store is bad; losing the state
      // replace for every store after it is far worse. Log and keep going.
      logResetFailure('reset', index, error)
    }

    try {
      store.setState(store.getInitialState(), true)
    } catch (error) {
      logResetFailure('setState', index, error)
    }
  })
}
